import {
  Component,
  effect,
  signal,
  computed,
  ElementRef,
  viewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CustomCapabilityConfigurationForm } from '../../../../../core/forms';

import { KeyValuePair } from '@vendasta/ai-assistants';
import {
  map,
  startWith,
  shareReplay,
  catchError,
} from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CrmFormService,
  FieldType,
} from '@galaxy/crm/static';
import { FieldSchemaInterface } from '@vendasta/crm';
import { of } from 'rxjs';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { StandardIds } from '@galaxy/crm/services/field-service';

export interface LeadCaptureField {
  fieldName: string;
  crmFieldId?: string;
  fieldType?: string;
}

const DEFAULT_FIELDS: LeadCaptureField[] = [
  { fieldName: 'First Name' },
  { fieldName: 'Last Name' },
  { fieldName: 'Phone' },
  { fieldName: 'Email' },
];

const DEFAULT_FIELD_NAMES = new Set(DEFAULT_FIELDS.map(field => field.fieldName));

const REFINED_FIELD_NAME_TO_ID_MAP = new Map<string, string | undefined>([
  ['Additional emails', StandardIds.ContactAdditionalEmails],
  ['Birth Year', StandardIds.ContactBirthYear],
  ['Gender', StandardIds.ContactGender],
  ['Interests', StandardIds.ContactInterests],
  ['Job title', StandardIds.ContactJobTitle],
  ['LinkedIn URL', StandardIds.ContactLinkedInURL],
  ['Role', StandardIds.ContactRole],
  ['Skills', StandardIds.ContactSkills],
  ['Timezone', StandardIds.ContactTimezone],
]);

const REFINED_FIELD_IDS = new Set(REFINED_FIELD_NAME_TO_ID_MAP.values());

@Component({
  selector: 'ai-lead-capture-capability-form',
  imports: [
    ReactiveFormsModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    TranslateModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule
],
  providers: [CrmFormService],
  templateUrl: './lead-capture-capability-form.component.html',
  styleUrl: './lead-capture-capability-form.component.scss',
})
export class LeadCaptureCapabilityFormComponent extends CustomCapabilityConfigurationForm {
  private readonly crmFormService = inject(CrmFormService, { optional: true });
  protected readonly selectedFields = signal<LeadCaptureField[]>([...DEFAULT_FIELDS]);
  protected readonly fieldInput = viewChild<ElementRef<HTMLInputElement>>('fieldInput');
  protected readonly fieldSearchControl = new FormControl('');
  private readonly searchTerm = toSignal(this.fieldSearchControl.valueChanges.pipe(startWith('')), { initialValue: '' });
  private readonly crmFields$ = this.crmFormService
    ? this.crmFormService.listAllObjectFields$('Contact').pipe(
      map((fieldSchemas) => {
        return fieldSchemas
        .filter((schema) => this.shouldIncludeField(schema))
          .map(
            (schema) => {
              const predefinedFieldId = schema.fieldName ? REFINED_FIELD_NAME_TO_ID_MAP.get(schema.fieldName) : undefined;

              return {
                fieldName: schema.fieldName,
                crmFieldId: predefinedFieldId || schema.fieldId,
                fieldType: this.getFieldTypeString(schema.fieldType),
              } as LeadCaptureField;
            }
          );
      }),
      catchError((error) => {
        console.error('Failed to load CRM fields:', error);
        return of([] as LeadCaptureField[]);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
    : of([] as LeadCaptureField[]);

  protected readonly crmFields = toSignal(this.crmFields$, { initialValue: [] as LeadCaptureField[] });

  constructor() {
    super();
    effect(() => {
      const configForm = this.configurationForm();
      this.initializeFields(configForm);
    });
  }

  private isSystemField(namespace: string): boolean {
    return namespace === 'standard' || namespace === 'system' || namespace.startsWith('Extension');
  }

  protected isDefaultField(field: LeadCaptureField): boolean {
    return DEFAULT_FIELD_NAMES.has(field.fieldName);
  }

  private getFieldTypeString(fieldType: FieldType | undefined): string {
    if (!fieldType) {
      return '';
    }
    const typeString = fieldType.toString();
    switch (typeString) {
      case '1':
        return 'string';
      case '11':
        return 'list';
      default:
        return '';
    }
  }

  protected readonly availableFields = computed(() => {
    const selectedNames = new Set(this.selectedFields().map((f) => f.fieldName));
    return this.crmFields().filter((field) => !selectedNames.has(field.fieldName));
  });

  protected readonly filteredFields = computed(() => {
    const searchTerm = this.searchTerm();

    if (!searchTerm) {
      return this.availableFields();
    }

    const filterValue = searchTerm.toLowerCase();
    return this.availableFields().filter((field) =>
      field.fieldName.toLowerCase().includes(filterValue)
    );
  });

  private initializeFields(configForm: FormControl<any>) {
    const existingConfig = configForm.value || [];
    const configMap = new Map(existingConfig.map((kv: any) => [kv.key, kv.value]));

    const fieldsConfig: unknown = configMap.get('field_to_extract');
    let customFields: LeadCaptureField[] = [];

    if (fieldsConfig && typeof fieldsConfig === 'string') {
      try {
        const parsed: unknown = JSON.parse(fieldsConfig);
        if (Array.isArray(parsed)) {
          customFields = parsed as LeadCaptureField[];
        }
      } catch (e) {
        console.warn('Failed to parse lead capture fields config, using defaults', e);
      }
    }

    this.selectedFields.set([...DEFAULT_FIELDS, ...customFields]);
  }

  protected selectField(event: MatAutocompleteSelectedEvent): void {
    const field = event.option.value as LeadCaptureField;
    this.selectedFields.update((fields) => [...fields, field]);
    this.fieldSearchControl.setValue('');
    this.fieldInput()?.nativeElement.blur();
    this.saveConfiguration();
  }

  protected removeField(field: LeadCaptureField): void {
    if (this.isDefaultField(field)) {
      return;
    }
    this.selectedFields.update(fields => fields.filter(f => f.fieldName !== field.fieldName));
    this.saveConfiguration();
  }

  private saveConfiguration() {
    const configForm = this.configurationForm();
    const customFields = this.selectedFields().filter(field => !this.isDefaultField(field));
    const config: KeyValuePair[] = customFields.length === 0
      ? []
      : [
          new KeyValuePair({
            key: 'field_to_extract',
            value: JSON.stringify(customFields),
          }),
        ];

    configForm.setValue(config);
    configForm.markAsDirty();
  }

  private shouldIncludeField(schema: FieldSchemaInterface): boolean {
    const { fieldId, fieldName, namespace, fieldType } = schema;

    if (!fieldId || !fieldName) return false;
    if (DEFAULT_FIELD_NAMES.has(fieldName)) return false;
    if (fieldType !== FieldType.FIELD_TYPE_STRING && fieldType !== FieldType.FIELD_TYPE_STRING_LIST) return false;
    if (REFINED_FIELD_IDS.has(fieldId)) return true;

    return !this.isSystemField(namespace || '');
  }
}

